import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthLogoHeader from "@/components/auth/AuthLogoHeader";
import AuthPageShell from "@/components/auth/AuthPageShell";
import AuthPasswordField from "@/components/auth/AuthPasswordField";
import AuthSubmitButton from "@/components/auth/AuthSubmitButton";
import ResetPasswordForm from "@/components/forms/ResetPasswordForm";
import { userResetPassword } from "@/services/auth.service";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import {
    PASSWORD_RESET_ERR_MSGS,
    RESET_PASSWORD_SUCCESS_MESSAGE,
} from "@/utils/constants/messages";
import { checkPassword } from "@/utils/functions/helperFunctions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import { usePassword } from "@/utils/hooks/usePassword";
import { fetchConfig } from "@/services/setting.service";
import { getUserById } from "@/services/user.service";
import { getUserId } from "@/utils/functions/TokenFunction";
import { Label } from "@/components/ui/label";

const ChangePassword = () => {
    const [, , currentPassword, setCurrentPassword] = usePassword();
    const [, , newPassword, setNewPassword] = usePassword();
    const [done, setDone] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const [user, setUser] = useState<any>({});
    const [organizationSetting, setOrganizationSetting] = useState<any>({});
    const [userLoaded, setUserLoaded] = useState(false);

    useEffect(() => {
        const userId = getUserId();
        if (!userId) {
            setUserLoaded(true);
            return;
        }

        fetchConfig(
            null,
            (res: any) => {
                setOrganizationSetting(res);
            },
            (err: any) => {
                errorToasterMessage(err?.response?.data?.message);
            }
        );
        getUserById(
            userId,
            (res: any) => {
                setUser(res.data);
                setUserLoaded(true);
            },
            () => {
                setUserLoaded(true);
            }
        );
    }, []);

    const handleReset = (e: React.FormEvent<HTMLFormElement>) => {
        setDone(true);
        e.preventDefault();
        if (!newPassword) {
            setDone(false);
            setErrMsg(PASSWORD_RESET_ERR_MSGS.BLANK_NEW_PASSWORD);
        } else if (!currentPassword) {
            setDone(false);
            setErrMsg(PASSWORD_RESET_ERR_MSGS.BLANK_CURRENT_PASSWORD);
        } else {
            const result = checkPassword(newPassword as string);
            if (result) {
                setDone(false);
                setErrMsg(result);
                return;
            }
            if (newPassword === currentPassword) {
                setDone(false);
                setErrMsg(PASSWORD_RESET_ERR_MSGS.SAME_AS_PREVIOUS);
                return;
            }
            userResetPassword(
                currentPassword,
                newPassword,
                (res: any) => {
                    setDone(false);
                    if (res.status_code === "000") {
                        setErrMsg(RESET_PASSWORD_SUCCESS_MESSAGE);
                        successToasterMessage(RESET_PASSWORD_SUCCESS_MESSAGE);
                    } else {
                        setErrMsg(res.message);
                    }
                },
                () => {
                    setDone(false);
                    setErrMsg(PASSWORD_RESET_ERR_MSGS.INCORRECT_CURRENT_PASSWORD);
                }
            );
        }
    };

    const handleCurrPass = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value.length === 0) {
            setErrMsg(PASSWORD_RESET_ERR_MSGS.BLANK_CURRENT_PASSWORD);
        } else {
            setErrMsg("");
        }
        setCurrentPassword(value);
    };

    const handleNewPass = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value.length === 0) {
            setErrMsg(PASSWORD_RESET_ERR_MSGS.BLANK_NEW_PASSWORD);
        } else {
            setErrMsg("");
        }
        setNewPassword(value);
    };

    const heading =
        userLoaded &&
        organizationSetting?.is_directory_service_enabled &&
        !user?.has_password
            ? HEADINGS.SET_PASSWORD
            : HEADINGS.CHANGE_PASSWORD;

    const showSetPasswordFlow =
        userLoaded &&
        organizationSetting?.is_directory_service_enabled &&
        !user?.has_password;

    return (
        <AuthPageShell>
            <AuthLogoHeader id="change-password" heading={heading} />

            {showSetPasswordFlow ? (
                <>
                    <ResetPasswordForm title="change-password" />
                    <div className="gs-auth-link-wrap">
                        <Link to="/" className="gs-auth-link">
                            <span id="change-password-form-back-to-dashboard-btn">
                                {BUTTONS.BACK_TO_DASHBOARD}
                            </span>
                        </Link>
                    </div>
                </>
            ) : (
                <div className="gs-auth-form">
                    {errMsg !== RESET_PASSWORD_SUCCESS_MESSAGE ? (
                        <>
                            <form onSubmit={handleReset}>
                                <div className="gs-auth-field">
                                    <Label
                                        htmlFor="change-password-form-current-password-input"
                                        id="change-password-form-current-password-label"
                                        className="gs-auth-label"
                                    >
                                        {LABELS.CURRENT_PASSWORD}
                                    </Label>
                                    <AuthPasswordField
                                        id="change-password-form-current-password-input"
                                        value={currentPassword as string}
                                        onChange={handleCurrPass}
                                        placeholder="Enter your current password"
                                        required
                                    />
                                </div>

                                <div className="gs-auth-field">
                                    <Label
                                        htmlFor="change-password-form-new-password-input"
                                        id="change-password-form-new-password-label"
                                        className="gs-auth-label"
                                    >
                                        {LABELS.NEW_PASSWORD}
                                    </Label>
                                    <AuthPasswordField
                                        id="change-password-form-new-password-input"
                                        value={newPassword as string}
                                        onChange={handleNewPass}
                                        placeholder="Enter your new password"
                                        autoFocus={false}
                                        required
                                    />
                                </div>

                                <div className="gs-auth-field">
                                    <AuthSubmitButton
                                        id="change-password-form-btn"
                                        label={BUTTONS.CHANGE_PASSWORD}
                                        loading={done}
                                    />
                                </div>

                                {errMsg ? (
                                    <p
                                        className="gs-auth-error"
                                        id="change-password-form-error-msg"
                                    >
                                        {errMsg}
                                    </p>
                                ) : null}
                            </form>

                            <div className="gs-auth-link-wrap">
                                <Link to="/" className="gs-auth-link">
                                    <span id="change-password-form-back-to-dashboard-btn">
                                        {BUTTONS.BACK_TO_DASHBOARD}
                                    </span>
                                </Link>
                            </div>
                        </>
                    ) : (
                        <div className="gs-auth-success">
                            <p className="gs-auth-message" id="change-password-success-error-msg">
                                {errMsg}
                            </p>
                            <Link
                                to="/dashboard"
                                className="gs-auth-submit-btn gs-auth-submit-btn--link"
                                id="change-password-success-back-to-dashboard-btn"
                            >
                                {BUTTONS.BACK_TO_DASHBOARD}
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </AuthPageShell>
    );
};

export default ChangePassword;
